import { path } from 'ramda'
import { APP_VERSION, BUSINESS_STATUSES, EVENTS } from '@/lib/consts.js'
import GoogleTagManager from './google-tag-manager.js'
import { gtm1Appointment, BUSINESS_BOOKING_ACTION_EVENT } from './appointment/gtm1-appointment.js'
import { limitBusinessBookingParams } from './appointment/limit-business-booking-params'
import { gtm1Onboarding } from './onboarding/gtm1-onboarding.js'
import { gtm1Checkout } from './checkout/gtm1-checkout.js'
import { gtm1Payments } from '@/lib/analytics/payments/gtm1-payments.js'
import { gtm1BooksyCardReader } from './booksy-card-reader/gtm1-booksy-card-reader.js'
import { gtm1BestOfBooksy } from './best-of-booksy/gtm1-best-of-booksy.js'
import { gtm1Subscription } from './subscription/gtm1-subscription.js'
import { gtm1SocialPostCreator } from './social-post-creator/gtm-1-social-post-creator.js'
import { gtm1Marketing } from './marketing/gtm1-marketing.js'
import {
  getAddress,
  getDeviceType,
  getEventDate,
  getExternalId,
  isBusinessOpenedToday,
  setOpenedBusinessData,
  simplifyCategory,
  getBusinessPrefixedPhone,
  getRegistrationMethod,
} from '@/lib/analytics/helpers.js'
import { KYC_STATUS, EVENT_ACTIONS } from '@/lib/analytics/consts.js'

export default class GoogleTagManager1 extends GoogleTagManager {
  constructor (config, manager) {
    super({
      ...config,
      eventMiddlewares: {
        ...config.eventMiddlewares,
        [BUSINESS_BOOKING_ACTION_EVENT]: limitBusinessBookingParams,
      },
    }, manager)
    Object.assign(this, gtm1Appointment)
    Object.assign(this, gtm1Onboarding)
    Object.assign(this, gtm1Checkout)
    Object.assign(this, gtm1BooksyCardReader)
    Object.assign(this, gtm1BestOfBooksy)
    Object.assign(this, gtm1Subscription)
    Object.assign(this, gtm1SocialPostCreator)
    Object.assign(this, gtm1Payments)
    Object.assign(this, gtm1Marketing)
  }

  async eventLoginHandler ({ user, country, handlerData, primaryCategory, routingConfig }) {
    const business_primary_category = simplifyCategory(primaryCategory)

    return {
      event: 'Business_Loggedin',
      properties: {
        business_primary_category,
      },
    }
  }

  async eventBusinessCategoriesUpdatedHandler ({ data, user, categories, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const primary_category = categories.find(category => category.id === business.primary_category)
    const business_primary_category = simplifyCategory(primary_category)
    const simplifiedCategories = categories.map(simplifyCategory)

    return {
      event: 'Business_Categories_Updated',
      properties: {
        business_name: business.name,
        business_primary_category,
        categories: simplifiedCategories,
        email: user.email,
      },
    }
  }

  eventCustomerAppointmentChangeStatusNoShowHandler ({ data, business, country, device, primaryCategory, routingConfig }) {
    const business_primary_category = simplifyCategory(primaryCategory)
    const appointment = path(['appointment'], data) || {}
    const booking = path(['subbookings', '0'], appointment) || {}
    const bookingService = path(['service'], booking) || {}

    return {
      event: 'CB_No_Show_For_Business',
      properties: {
        business_primary_category,
        currency: data.currency,
        price: appointment.total_value,
        service_id: bookingService.id,
      },
    }
  }

  eventBusinessAppointmentChangeStatusNoShowHandler ({ data, business, country, device, primaryCategory, routingConfig }) {
    const business_primary_category = simplifyCategory(primaryCategory)
    const appointment = path(['appointment'], data) || {}
    const booking = path(['subbookings', '0'], appointment) || {}
    const bookingService = path(['service'], booking) || {}

    return {
      event: 'BB_No_Show_For_Business',
      properties: {
        business_primary_category,
        currency: data.currency,
        price: appointment.total_value,
        service_id: bookingService.id,
      },
    }
  }

  eventAppointmentAddedHandler ({ data, business, country, device, primaryCategory, categories, routingConfig }) {
    const business_primary_category = simplifyCategory(primaryCategory)
    const bookingCounters = path(['bookingCounters'], data) || {}

    const payload = {
      properties: {
        address: getAddress(business.location),
        business_name: business.name,
        business_primary_category,
        categories: categories.map(simplifyCategory),
        email: business.owner_email,
      },
    }

    const events = {
      1: '1st_BB_Created_For_Business',
      2: '2nd_BB_Created_For_Business',
      3: '3rd_BB_Created_For_Business',
      5: '5th_BB_Created_For_Business',
      10: '10th_BB_Created_For_Business',
    }

    const eventName = events[bookingCounters.bb_booking_count]

    if (eventName) {
      return {
        event: eventName,
        ...payload,
      }
    }

    return this.sendingSkipped
  }

  eventBusinessInfoUpdatedHandler ({ data, country, device, routingConfig }) {
    const business = path(['business'], data) || {}
    const address = getAddress(business.location)
    const location = path(['location'], business) || {}

    return {
      event: 'Business_Info_Updated',
      properties: {
        address,
        business_name: business.name,
        city: location.city,
        owner_email: business.owner_email,
        owner_name: business.owner_full_name,
        owner_phone: getBusinessPrefixedPhone({ business, country }),
        // postalCode is named with camel case convencion, because Segment recognizes this as special value
        postalCode: location.zipcode,
      },
    }
  }

  eventMobilePaymentsGetStartedSelectedHandler ({ appType, business, country, device, routingConfig }) {
    business = business || {}

    const business_id = getExternalId({ id: business.id, country, routingConfig })
    const device_type = getDeviceType(device)

    return {
      event: 'KYC_Enable_Mobile_Payments_Started',
      properties: {
        app_version: APP_VERSION,
        business_id,
        business_kyc_status: KYC_STATUS.STARTED,
        country,
        device,
        device_type,
        email: business.owner_email,
      },
    }
  }

  eventAccessTokenErrorHandler ({ data, user, country, device, routingConfig }) {
    user = user || {}
    const device_type = getDeviceType(device)
    const accessToken = path(['accessToken'], data) || ''
    const access_token = accessToken.slice(-6)

    return {
      event: 'Expired_Session_Token',
      properties: {
        access_token,
        app_version: APP_VERSION,
        country,
        device,
        device_type,
      },
    }
  }

  eventBusinessUseAppPopupShowedHandler () {
    return { event: 'Business_Use_App_Popup_Showed' }
  }

  eventBusinessUseAppPopupOpenClickedHandler () {
    return { event: 'Business_Use_App_Popup_Open_Clicked' }
  }

  eventSimplifiedReferralOpenedHandler () {
    return { event: 'Simplified_Referral_Opened' }
  }

  eventSimplifiedReferralCodeClickedHandler () {
    return { event: 'Simplified_Referral_Code_Clicked' }
  }

  eventSimplifiedReferralShareClickedHandler () {
    return { event: 'Simplified_Referral_Share_Clicked' }
  }

  eventInventoryProductAddBarcodeClickedHandler () {
    return { event: 'Inventory_Product_Add_Barcode_Clicked' }
  }

  eventInventoryProductEditBarcodeClickedHandler () {
    return { event: 'Inventory_Product_Edit_Barcode_Clicked' }
  }

  eventTransactionListShown () {
    return { event: 'Transaction_List_Shown' }
  }

  eventBizSettingsActionHandler ({ data }) {
    return {
      event: 'Biz_Settings_Action',
      properties: {
        event_action: data.eventAction,
        screen_name: data.screenName,
        category_name: data.categoryName,
        status: data.status,
        is_deposit_setup: data.hasPpRules,
        is_cf_setup: data.hasCfRules,
        health_and_safety_rules: data.health_and_safety_rules,
        booking_policies_rules: data.booking_policies_rules,
        custom_booking_policies: data.custom_booking_policies,
        additional_details: data.additional_details,
        rule_category: data.rule_category,
        rule_id: data.rule_id,
        rule_text: data.rule_text,
        payment_provider: data.paymentProvider,
        kyc_banner_shown: data.kycBannerShown,
        sub_label: data.subLabel,
      },
    }
  }

  eventNoShowProtectionActionHandler ({ data }) {
    return {
      event: 'No_Show_Protection_Action',
      properties: {
        event_action: data.eventAction,
        screen_name: data.screenName,
        rules_number_percentage: data.rulesNumberPercentage,
        rules_number_fixed_amount: data.rulesNumberFixedAmount,
        rules_value_percentage: data.rulesValuePercentage,
        rules_value_fixed_amount: data.rulesValueFixedAmount,
        cancellation_window: data.cancellationWindow,
        auto_charge_cf: data.autoChargeCf,
        value_percentage: data.valuePercentage,
        value_fixed_amount: data.valueFixedAmount,
        services_number: data.servicesNumber,
        entrance_path: data.entrancePath,
        select_all: data.selectAll,
        modal_name: data.modalName,
        canceled_appointments: data.canceledAppointments,
        lost_revenue: data.lostRevenue,
        old_deposit_share: data.oldDepositShare,
        new_deposit_share: data.newDepositShare,
        apply_change_option: data.applyChangeOption,
        page_source: data.pageSource,
      },
    }
  }

  eventAdMainscreenActionHandler ({ data }) {
    return {
      event: 'AD_Mainscreen_Action',
      properties: {
        event_action: data.eventAction,
        addon_count: data.addonCount,
      },
    }
  }

  eventBSCComboServiceActionHandler ({ data }) {
    return {
      event: 'BSC_Combo_Service_Action',
      properties: {
        event_action: data?.event_action,
        screen_name: data?.screen_name,
        booking_type: data?.booking_type,
        service_pricing: data?.service_pricing,
        allow_book_online: data?.allow_book_online,
      },
    }
  }

  eventInitHandler ({ business }) {
    if (this.cannotSendAppOpened({ business })) {
      return this.sendingSkipped
    }

    const loggedToday = isBusinessOpenedToday({ business })

    if (loggedToday) {
      return this.sendingSkipped
    }

    setOpenedBusinessData({ business })
    return this.getAppOpenedPayload()
  }

  eventInviteProcessStartedHandler ({ data }) {
    return {
      event: 'Invite_Process_Started',
      properties: {
        event_date: getEventDate(),
        import_source: data.import_source,
        page_source: data.page_source,
        event_action: data.event_action,
      },
    }
  }

  eventAccessToAddressBookGrantedDeniedHandler ({ data }) {
    return {
      event: 'Access_To_Address_Book_Granted_Denied',
      properties: {
        permission_granted: data.permission_granted,
      },
    }
  }

  eventInviteProcessingStartedHandler ({ data }) {
    return {
      event: 'Invite_Processing_Started',
      properties: {
        import_source: data.import_source,
        page_source: data.page_source,
        event_action: data.event_action,
      },
    }
  }

  eventMessageBlastEnteredHandler ({ data }) {
    return {
      event: 'Message_Blast_Entered',
      properties: {
        source_tab: data.source_tab,
      },
    }
  }

  eventSubscriptionTrialSelectPlanClickedHandler ({ data }) {
    return {
      event: 'Subscription_Trial_Select_Plan_Clicked',
    }
  }

  eventFastPayoutsActionHandler ({ data }) {
    return {
      event: 'Fast_Payouts_Action',
      properties: {
        amount_changed: data.amountChanged,
        amount_value: data.amountValue,
        description_changed: data.descriptionChanged,
        event_action: data.eventAction,
        fast_payouts_changed: data.fastPayoutsChanged,
        fast_payouts_status: data.fastPayoutsStatus,
        fast_payouts_value: data.fastPayoutsValue,
        payout_type: data.payoutType,
        screen_name: data.screenName,
      },
    }
  }

  eventBusinessInviteSplashActionHandler ({ data }) {
    return {
      event: 'Business_Invite_Splash_Action',
      properties: {
        event_action: data.eventAction,
        page_source: data.pageSource,
      },
    }
  }

  eventTurnTrackerActionHandler ({ data }) {
    return {
      event: 'Turn_Tracker_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
      },
    }
  }

  eventLanguageSettingsActionHandler ({ data }) {
    const languageToActionLanguage = {
      'en': 'en-us',
    }

    return {
      event: 'Language_Settings_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        previous_screen: data.previousScreen,
        ...data.languageNew ? {
          language_new: languageToActionLanguage[data.languageNew] || data.languageNew,
        } : {},
        ...data.languageOld ? {
          language_old: languageToActionLanguage[data.languageOld] || data.languageOld,
        } : {},
      },
    }
  }

  eventCalendarSettingsActionHandler ({ data }) {
    return {
      event: 'Calendar_Settings_Action',
      properties: {
        screen_name: data.screenName ?? null,
        event_action: data.eventAction ?? null,
        default_view: data.defaultView ?? null,
        display_mode: data.displayMode ?? null,
        time_slots: data.timeSlots ?? null,
        color_scheme: data.colorScheme ?? null,
        old_view: data.oldView ?? null,
        new_view: data.newView ?? null,
        payments: data.payments ?? null,
        appointment_status: data.appointmentStatus ?? null,
        staff_selected: data.staffSelected ?? null,
        details: data.details ?? null,
        option_chosen: data.optionChosen ?? null,
        action_source: data.actionSource ?? null,
        resource_changed: data.resourceChanged ?? null,
        time_changed: data.timeChanged ?? null,
        toast_message: data.toastMessage ?? null,
        outdated_duration: data.outdatedDuration ?? null,
      },
    }
  }

  eventBusinessReportActionHandler ({ data }) {
    return {
      event: 'Business_Report_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        report_scope: data.reportScope,
        general_section: data.generalSection,
        appointment_status_section: data.appointmentStatusSection,
        sales_commissions_section: data.salesCommissionsSection,
        product_sales_section: data.productSalesSection,
        clients_reviews_section: data.clientsReviewsSection,
        client_satisfaction_section: data.clientSatisfactionSection,
        working_hours_section: data.workingHoursSection,
        performed_services_section: data.performedServicesSection,
        appointments_section: data.appointmentsSection,
      },
    }
  }

  [EVENTS.EXPERIMENT_ACTION] ({ data }) {
    return {
      event: 'Experiment_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        experiment_name: data.experimentName,
        assigned_services: data.assignedServices,
        all_services: data.allServices,
        services_changed: data.servicesChanged,
        services_newly_assigned: data.servicesNewlyAssigned,
        services_unassigned: data.servicesUnassigned,
        multi_counter: data.multiCounter,
        experiment_variant: data.experimentVariant,
        modal_name: data.modalName,
        appointment_id: data.appointmentId,
        // eppo payload
        subject: data.subject,
        subject_type: data.subjectType,
        variation: data.variation,
        assignment_timestamp: data.assignmentTimestamp,
        app_domain: data.appDomain,

        page_source: data.pageSource,
        target_type: data.targetType,
        business_hours_completed: data.business_hours_completed,
        cover_photo_completed: data.cover_photo_completed,
        portfolio_completed: data.portfolio_completed,
        services_completed: data.services_completed,
      },
    }
  }

  [EVENTS.SERVICES_ACTION] ({ data }) {
    return {
      event: 'Services_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        source: data.source,
        service_type_id: data.serviceTypeId,
        service_id: data.serviceId,
        text: data.text,
        no_of_variant: data.noOfVariant,
        service_variant_id: data.serviceVariantId,
        message_error: data.messageError,
      },
    }
  }

  [EVENTS.BUSINESS_HINTS_ACTION] ({ business, data }) {
    return {
      event: 'Business_Hints_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        business_is_live: !business.visible_delay_till,
        screen_index: data.screenIndex ?? null,
        path_finished: data.pathFinished ?? null,
        hint_path_finished: data.hintPathFinished ?? null,
        hint_index: data.hintIndex ?? null,
        hint_type: data.hintType ?? null,
        hint_name: data.hintName ?? null,
      },
    }
  }

  [EVENTS.BUSINESS_MAIN_TAB_ACTION] ({ data }) {
    return {
      event: 'Business_Main_Tab_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        instagram_display: data.instagramDisplay ?? null,
        instagram_agreed: data.instagramAgreed ?? null,
      },
    }
  }

  [EVENTS.BUSINESS_PORTFOLIO_ACTION] ({ data }) {
    return {
      event: 'Business_Portfolio_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        category_change: data.categoryChange ?? null,
        description_change: data.descriptionChange ?? null,
        upload_successful: data.uploadSuccessful ?? null,
        upload_source: data.uploadSource ?? null,
        number_of_uploaded_photos: data.numberOfUploadedPhotos ?? null,
        selected_photos: data.selectedPhotos ?? null,
        instagram_display: data.instagramDisplay ?? null,
        instagram_agreed: data.instagramAgreed ?? null,
      },
    }
  }

  [EVENTS.BOOST_ACTION] ({ data }) {
    return {
      event: 'Boost_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
      },
    }
  }

  [EVENTS.GOOGLE_BUSINESS_PROFILE_ACTION] ({ data }) {
    return {
      event: 'Google_Business_Profile_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        entry_source: data.entrySource,
        exit_source: data.exitSource,
        accounts_count: data.accountsCount,
        locations_count: data.locationsCount,
        match_name: data.matchName,
        match_address: data.matchAddress,
        match_category: data.matchCategory,
        status: data.status,
        previous_status: data.previousStatus,
        entry_screen_name: data.entryScreenName,
        verification_method: data.verificationMethod,
        sms_available: data.smsAvailable,
        phone_call_available: data.phoneCallAvailable,
        email_available: data.emailAvailable,
        resend_available: data.resendAvailable,
      },
    }
  }

  [EVENTS.BUSINESS_MESSAGE_BLASTS_ACTION] ({ data }) {
    return {
      event: 'Business_Message_Blasts_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
      },
    }
  }

  [EVENTS.BUSINESS_PROMOTIONS_ACTION] ({ data }) {
    return {
      event: 'Business_Promotions_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        modal_type: data.modalType,
      },
    }
  }

  [EVENTS.ONLINE_BOOKING_ACTION] ({ data }) {
    return {
      event: 'Online_Booking_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        ...(data.searchVisibility !== null && { search_visibility: data.searchVisibility }),
        ...(data.facebookActive !== null && { facebook_active: data.facebookActive }),
        ...(data.googleActive !== null && { google_active: data.googleActive }),
        ...(data.withFbToken !== null && { with_fb_token: data.withFbToken }),
        ...(data.canBeActivated !== null && { can_be_activated: data.canBeActivated }),
        ...(data.entrySource !== null && { entry_source: data.entrySource }),
      },
    }
  }

  [EVENTS.GIFT_CARD_ACTION] ({ data }) {
    return {
      event: 'Gift_Card_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        online_payments: data.onlinePayments,
        selling_gc: data.sellingGc,
        in_person_available: data.inPersonAvailable,
      },
    }
  }

  [EVENTS.CS_CHAT_CLICKED] ({ data }) {
    return {
      event: 'CS_Chat_Clicked',
      properties: {
        screen_name: data.screenName,
      },
    }
  }

  [EVENTS.LOGIN_LOGOUT_ACTION] ({ data }) {
    const eventReason = data.eventAction === EVENT_ACTIONS.USER_LOGGED
      ? getRegistrationMethod(data.eventReason)
      : data.eventReason

    const loginMethod = data.loginMethod
      ? getRegistrationMethod(data.loginMethod)
      : undefined

    return {
      event: 'Login_Logout_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        event_reason: eventReason,
        login_method: loginMethod,
      },
    }
  }

  [EVENTS.BUSINESS_MARKETING_ACTION] ({ data }) {
    return {
      event: 'Business_Marketing_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
      },
    }
  }

  [EVENTS.ECOMMERCE_ACTION] ({ data }) {
    return {
      event: 'Ecommerce_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
      },
    }
  }

  [EVENTS.ECOMMERCE_STORE_ACTION] ({ data }) {
    const { _eventName, properties } = data

    return {
      event: _eventName,
      _sendRaw: true,
      properties,
    }
  }

  [EVENTS.PROVIDER_TEMPORARY_ACTION] ({ data }) {
    return {
      event: 'Provider_Temporary_Action',
      properties: {
        screen_name: data.screenName,
        ...(data.eventAction && { event_action: data.eventAction }),
        ...(data.visibilityTileViewed && { visibility_tile_viewed: data.visibilityTileViewed }),
        ...(data.feedbackOption && { feedback_option: data.feedbackOption }),
        ...(data.customAnswer && { custom_answer: data.customAnswer }),
        ...(data.chosenItem && { chosen_item: data.chosenItem }),
        ...(data.offerId && { offer_id: data.offerId }),
        ...(data.visibilityStatus && { visibility_status: data.visibilityStatus }),
      },
    }
  }

  [EVENTS.BUSINESS_CUSTOMER_INFO_ACTION] ({ data }) {
    return {
      event: 'Business_Customer_Info_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        booksy_user: data.booksyUser,
        entry_source: data.entrySource,
        compact: data.compact,
      },
    }
  }

  [EVENTS.LOYALTY_CARD_ACTION] ({ data }) {
    return {
      event: EVENTS.LOYALTY_CARD_ACTION,
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        loyalty_cards_activated: data.loyaltyCardsActivated || null,
        loyalty_program_id: data.loyaltyProgramId || null,
        click_source: data.clickSource || null,
      },
    }
  }

  [EVENTS.WRAPPED_ACTION] ({ data }) {
    return {
      event: 'Wrapped_Action',
      properties: {
        screen_name: data.screenName,
        event_action: data.eventAction,
        ...(data.pageSource && { page_source: data.pageSource }),
        ...(data.pageName && { page_name: data.pageName }),
      },
    }
  }

  // helper methods

  // Business_App_Opened event works:
  // 1. Each time when user manually logs into the configured business.
  // 2. When user enter's dashboard after configuring the business.
  // 3. When configured business is already logged and visits page for the first time
  // during the current day.
  getAppOpenedPayload () {
    const payload = {
      event: 'Business_App_Opened',
    }

    return payload
  }

  cannotSendAppOpened ({ business }) {
    return !business?.id || business?.status === BUSINESS_STATUSES.SETUP
  }
}
